import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import type { Resolver } from 'react-hook-form'

import type { ExpenseCategory } from '@/lib/api/expense-category-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ListCard } from '@/components/common/ListCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateExpenseCategory,
    useDeleteExpenseCategory,
    useExpenseCategories,
    useUpdateExpenseCategory,
} from '@/hooks/use-expense-category-query'
import { m } from '@/lib/paraglide/messages'
import { createExpenseCategorySchema } from '@/lib/schemas/expense-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/expense-categories',
)({
    component: ExpenseCategoriesPage,
})

type FormValues = {
    code: string
    name: string
    description?: string
    is_active: boolean
}

function ExpenseCategoriesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('expense_categories.write')
    const [editing, setEditing] = useState<ExpenseCategory | null>(null)
    const [open, setOpen] = useState(false)

    const { data } = useExpenseCategories({ per_page: 100 })
    const rows = data?.data ?? []

    const createMut = useCreateExpenseCategory()
    const updateMut = useUpdateExpenseCategory(editing?.id ?? '')
    const deleteMut = useDeleteExpenseCategory()

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.expense_category_title()}
                    </h1>
                </div>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.expense_category_new()}
                    </Button>
                )}
            </div>

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {rows.map((row) => (
                    <ListCard
                        key={row.id}
                        title={
                            <span className="font-medium break-words">
                                {row.name}
                            </span>
                        }
                        subtitle={row.code}
                        badge={
                            <span className="flex flex-wrap gap-1">
                                <Badge
                                    variant={
                                        row.is_active ? 'default' : 'secondary'
                                    }
                                >
                                    {row.is_active
                                        ? m.expense_category_active()
                                        : m.customer_inactive()}
                                </Badge>
                                {row.system_key && (
                                    <Badge variant="outline">
                                        {m.expense_category_system_badge()}
                                    </Badge>
                                )}
                            </span>
                        }
                        actions={
                            canWrite ? (
                                <>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label={m.common_edit()}
                                        onClick={() => {
                                            setEditing(row)
                                            setOpen(true)
                                        }}
                                    >
                                        <Pencil className="size-4" />
                                    </Button>
                                    {!row.system_key && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={m.common_delete()}
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        m.expense_delete_confirm(),
                                                    )
                                                ) {
                                                    deleteMut.mutate(row.id)
                                                }
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    )}
                                </>
                            ) : null
                        }
                        fields={[
                            {
                                label: m.expense_category_description(),
                                value: row.description ?? '—',
                                full: true,
                            },
                        ]}
                    />
                ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.expense_category_code()}</TableHead>
                            <TableHead>{m.expense_category_name()}</TableHead>
                            <TableHead>
                                {m.expense_category_description()}
                            </TableHead>
                            <TableHead>{m.expense_category_active()}</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell className="font-mono">
                                    {row.code}
                                </TableCell>
                                <TableCell>
                                    {row.name}
                                    {row.system_key && (
                                        <Badge
                                            variant="outline"
                                            className="ml-2"
                                        >
                                            {m.expense_category_system_badge()}
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell className="max-w-xs truncate">
                                    {row.description ?? '—'}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            row.is_active
                                                ? 'default'
                                                : 'secondary'
                                        }
                                    >
                                        {row.is_active ? '✓' : '—'}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right space-x-1">
                                    {canWrite && (
                                        <>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => {
                                                    setEditing(row)
                                                    setOpen(true)
                                                }}
                                            >
                                                <Pencil className="size-4" />
                                            </Button>
                                            {!row.system_key && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        if (
                                                            window.confirm(
                                                                m.expense_delete_confirm(),
                                                            )
                                                        ) {
                                                            deleteMut.mutate(
                                                                row.id,
                                                            )
                                                        }
                                                    }}
                                                >
                                                    <Trash2 className="size-4" />
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing
                                ? m.expense_category_edit()
                                : m.expense_category_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <CategoryForm
                        initial={editing}
                        onSubmit={async (values) => {
                            if (editing) {
                                await updateMut.mutateAsync(values)
                            } else {
                                await createMut.mutateAsync(values)
                            }
                            setOpen(false)
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}

function CategoryForm({
    initial,
    onSubmit,
    submitting,
}: {
    initial: ExpenseCategory | null
    onSubmit: (v: FormValues) => Promise<void>
    submitting: boolean
}) {
    const schema = createExpenseCategorySchema()
    const form = useForm<FormValues>({
        // zodResolver overloads expect either zod v3 or zod v4 (minor=0) schema
        // types; expense-schemas.ts uses zod v4 which reports minor=3 in its
        // internal type brand. Cast through unknown to bypass the overload check —
        // the runtime behavior is identical.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resolver: zodResolver(schema as any) as Resolver<FormValues>,
        defaultValues: {
            code: initial?.code ?? '',
            name: initial?.name ?? '',
            description: initial?.description ?? '',
            is_active: initial?.is_active ?? true,
        },
    })
    const locked = Boolean(initial?.system_key)
    return (
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            {locked && (
                <p className="text-xs text-muted-foreground">
                    {m.expense_category_system_locked_hint()}
                </p>
            )}
            <div>
                <Label>{m.expense_category_code()}</Label>
                <Input {...form.register('code')} disabled={locked} />
                {form.formState.errors.code && (
                    <p className="text-xs text-red-500">
                        {form.formState.errors.code.message}
                    </p>
                )}
            </div>
            <div>
                <Label>{m.expense_category_name()}</Label>
                <Input {...form.register('name')} disabled={locked} />
                {form.formState.errors.name && (
                    <p className="text-xs text-red-500">
                        {form.formState.errors.name.message}
                    </p>
                )}
            </div>
            <div>
                <Label>{m.expense_category_description()}</Label>
                <textarea
                    {...form.register('description')}
                    className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
                    rows={3}
                />
            </div>
            <div className="flex items-center gap-2">
                <Switch
                    checked={form.watch('is_active')}
                    onCheckedChange={(v) => form.setValue('is_active', v)}
                    disabled={locked}
                />
                <Label>{m.expense_category_active()}</Label>
            </div>
            <DialogFooter>
                <Button type="submit" disabled={submitting}>
                    {submitting
                        ? '...'
                        : initial
                          ? m.common_save()
                          : m.expense_category_new()}
                </Button>
            </DialogFooter>
        </form>
    )
}
